/**
 * Copyright Highway9 Networks Inc.
 * Custom Toast Notification Component
 */
import React, { forwardRef } from 'react';
import { SnackbarContent, CustomContentProps, closeSnackbar, SnackbarKey } from 'notistack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseIcon from '@mui/icons-material/Close';

import { Box, Button, Chip, Divider, IconButton, Typography } from '@h9/ui-lib';

interface CustomToastProps extends Partial<CustomContentProps> {
  // id and message stay required; all other notistack-injected fields become optional
  // so CustomToast can be rendered both through the registry and via a content callback
  id: CustomContentProps['id'];
  message: CustomContentProps['message'];
  allowDownload?: boolean;
  /** Extra content rendered below the message (e.g. an action list). */
  actions?: React.ReactNode;
  /** Called when the user explicitly dismisses the toast via the close button. */
  onDismiss?: () => void;
}

/**
 * CustomToast Component
 * A minimal, modern toast notification with clean design.
 * Supports an optional `actions` slot below the message and an `onDismiss` callback.
 */
export const CustomToast = forwardRef<HTMLDivElement, CustomToastProps>(
  ({ id, message, variant = 'default', actions, onDismiss, ...props }, ref) => {
    const getIcon = () => {
      switch (variant) {
        case 'success': return <CheckCircleIcon sx={{ fontSize: 20 }} />;
        case 'error':   return <ErrorIcon       sx={{ fontSize: 20 }} />;
        case 'warning': return <WarningIcon     sx={{ fontSize: 20 }} />;
        case 'info':    return <InfoIcon        sx={{ fontSize: 20 }} />;
        default:        return <InfoIcon        sx={{ fontSize: 20 }} />;
      }
    };

    const getColor = () => {
      switch (variant) {
        case 'success': return { bg: '#E0FFF0', border: 'success.main', icon: 'success.main', text: 'success.dark' };
        case 'error':   return { bg: '#FFE0E4', border: 'error.main',   icon: 'error.main',   text: 'error.dark'   };
        case 'warning': return { bg: '#FFEFE0', border: 'warning.main', icon: 'warning.main', text: 'warning.dark' };
        case 'info':    return { bg: '#E2F2FF', border: 'info.main',    icon: 'info.main',    text: 'info.dark'    };
        default:        return { bg: 'background.paper', border: 'divider', icon: 'text.primary', text: 'text.primary' };
      }
    };

    const colors = getColor();
    const hasActions = Boolean(actions);

    return (
      <SnackbarContent ref={ref} role="alert" {...props}>
        <Box
          sx={{
            display: 'flex',
            // top-align when actions expand the height; center for single-line toasts
            alignItems: hasActions ? 'flex-start' : 'center',
            gap: 1.5,
            bgcolor: colors.bg,
            border: 1,
            borderColor: colors.border,
            borderRadius: 2,
            px: 2,
            py: 1.25,
            minWidth: 300,
            maxWidth: 500,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': { boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)' },
          }}
        >
          {/* Icon — slight top offset so it sits on the text baseline, not above it */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.icon,
              flexShrink: 0,
              ...(hasActions && { mt: 0.2 }),
            }}
          >
            {getIcon()}
          </Box>

          {/* Message + optional actions slot */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="body2"
              sx={{ color: colors.text, fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 }}
            >
              {message}
            </Typography>
            {actions}
          </Box>

          {/* Close button */}
          <IconButton
            size="small"
            onClick={() => { onDismiss?.(); closeSnackbar(id); }}
            sx={{
              padding: 0.5,
              color: colors.icon,
              flexShrink: 0,
              alignSelf: 'flex-start',
              '&:hover': { bgcolor: (theme) => theme.alpha(theme.palette.action.hover, 0.5) },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </SnackbarContent>
    );
  }
);

CustomToast.displayName = 'CustomToast';

export default CustomToast;

// ---------------------------------------------------------------------------
// Generic warning-list toast
// Use this for any module that needs to show a persistent warning with a
// vertically-stacked list of items and a single footer action.
// ---------------------------------------------------------------------------

export interface WarningListItem {
  /** Primary label (e.g. edge name, device name). */
  primary: string;
  /** Optional secondary label shown as a small chip (e.g. release version). */
  secondary?: string;
}

export interface WarningListToastContentProps {
  id: SnackbarKey;
  /** Bold amber title line (e.g. "3 edge releases going out of support in next upgrade."). */
  title: string;
  /** Items to display vertically. Only the first `maxItems` are shown. */
  items: WarningListItem[];
  /** Maximum items shown before a "+N more" link. Defaults to 2. */
  maxItems?: number;
  /** Label for the footer action button. Defaults to "View all". */
  actionLabel?: string;
  /** Called when the footer action or "+N more" is clicked. */
  onAction?: () => void;
  /** Called when the close button is clicked. */
  onDismiss: () => void;
}

export const WarningListToastContent = forwardRef<HTMLDivElement, WarningListToastContentProps>(
  ({ id, title, items, maxItems = 2, actionLabel = 'View all', onAction, onDismiss }, ref) => {
    const visibleItems = items.slice(0, maxItems);
    const hiddenCount = items.length - visibleItems.length;

    return (
      <SnackbarContent ref={ref} role="alert">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#FFF4E5',
            border: '1px solid',
            borderColor: 'warning.main',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            px: 2,
            py: 1.5,
            gap: 0.75,
            minWidth: '30vw',
            maxWidth: 460,
          }}
        >
          {/* Title row: icon · title · close */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0 }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'warning.main', lineHeight: 1.4, flex: 1, fontSize: '14px' }}
            >
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={() => { onDismiss(); closeSnackbar(id); }}
              sx={{ p: 0.25, color: 'text.disabled', flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Body — indented once so the item list and footer button align with the title text */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: '24px' }}>
            {/* Item list — single horizontal row. Long names wrap within their own item
                instead of pushing "+N more" away, so it always sits right after the last item. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {visibleItems.map((item, index) => (
                <React.Fragment key={item.primary}>
                  {index > 0 && (
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', flexShrink: 0 }} />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.8rem',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.primary}
                    </Typography>
                    {item.secondary && (
                      <Chip
                        label={item.secondary}
                        size="small"
                        sx={{
                          bgcolor: 'transparent',
                          color: '#8C6A4A',
                          borderRadius: '100px',
                          fontSize: '0.7rem',
                          height: 20,
                          flexShrink: 0,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    )}
                  </Box>
                </React.Fragment>
              ))}
              {hiddenCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    +{hiddenCount} more
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer action */}
            {onAction && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button variant="text" size="small" onClick={onAction} sx={{ whiteSpace: 'nowrap', pl:0 }}>
                  {actionLabel}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </SnackbarContent>
    );
  }
);

WarningListToastContent.displayName = 'WarningListToastContent';
